import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ControlPointDuplicateIcon from "@mui/icons-material/ControlPointDuplicate";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CNVDetailTabs from "./CNVDetailTabs";

export default function CNVRow(props) {
  const data = props.row;

  // Helper function to format size
  const formatSize = (size) => {
    if (!size) return "N/A";
    if (size >= 1000000) {
      return `${(size / 1000000).toFixed(2)} Mb`;
    } else if (size >= 1000) {
      return `${(size / 1000).toFixed(2)} kb`;
    }
    return `${size} bp`;
  };

  // Get color based on clinical significance
  const getSignificanceColor = (significance) => {
    if (!significance) return "#747b87";
    const lower = significance.toLowerCase();
    if (lower.includes("pathogenic") && !lower.includes("likely")) {
      return "#ab0e0e";
    } else if (lower.includes("likely pathogenic")) {
      return "#eba721";
    } else if (lower.includes("benign")) {
      return "#1AB29C";
    } else if (lower.includes("uncertain")) {
      return "#747b87";
    }
    return "#747b87";
  };

  // Get icon based on CNV type
  const getCNVTypeIcon = (type) => {
    const iconProps = { sx: { fontSize: 32 } };
    switch (type?.toLowerCase()) {
      case "deletion":
        return (
          <RemoveCircleIcon
            {...iconProps}
            sx={{ fontSize: 22, color: "#8e8f91ff" }}
          />
        );
      case "duplication":
        return (
          <ControlPointDuplicateIcon
            {...iconProps}
            sx={{ fontSize: 22, color: "#8e8f91ff" }}
          />
        );
      case "amplification":
        return (
          <AddCircleIcon
            {...iconProps}
            sx={{ fontSize: 22, color: "#8e8f91ff" }}
          />
        );
      default:
        return (
          <HelpOutlineIcon
            {...iconProps}
            sx={{ fontSize: 22, color: "#8e8f91ff" }}
          />
        );
    }
  };

  return (
    <Box>
      <Accordion
        elevation={3}
        sx={{
          width: props.containerWidth - 10,
          borderRadius: "5px",
          marginX: "3px",
          marginTop: "3px",
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            marginX: "3px",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          onMouseDown={(event) => event.stopPropagation()}
          sx={{
            minHeight: "50px",
            padding: "0px",
            "& .MuiAccordionSummary-content": {
              margin: "0px",
              height: "50px",
              width: "100%",
            },
          }}
        >
          <Grid
            container
            sx={{
              display: "flex",
              height: "100%",
              flexWrap: "nowrap",
            }}
          >
            {/* CNV Type Icon */}
            <Grid item sx={{ height: "100%", width: "60px", flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  pl: 1,
                }}
              >
                <Tooltip title={data.cnv_type || "Unknown"} arrow>
                  {getCNVTypeIcon(data.cnv_type)}
                </Tooltip>
              </Box>
            </Grid>

            {/* Gene Symbol column */}
            <Grid
              item
              sx={{
                height: "100%",
                width: "180px",
                flexShrink: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  marginX: "10px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "left",
                  }}
                >
                  <Typography fontSize={"0.9rem"} fontWeight={600}>
                    {data.gene_symbol || "N/A"}
                  </Typography>
                  <Typography fontSize={"0.7rem"} color={"grey"}>
                    {data.chromosome || "N/A"}:{data.start_position}-
                    {data.end_position}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* CNV Type column */}
            <Grid item sx={{ height: "100%", width: "150px", flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    fontSize={"0.7rem"}
                    color={"grey"}
                    textAlign="center"
                  >
                    CNV Type
                  </Typography>
                  <Chip
                    label={data.cnv_type || "N/A"}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "black",
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Exons column */}
            <Grid item sx={{ height: "100%", width: "200px", flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    fontSize={"0.7rem"}
                    color={"grey"}
                    textAlign="center"
                  >
                    Affected Exons
                  </Typography>
                  <Typography fontSize={"0.8rem"}>
                    {data.exons || "N/A"}
                  </Typography>
                  {data.total_exons && (
                    <Typography fontSize={"0.65rem"} color={"grey"}>
                      ({data.start_exon}-{data.end_exon} of {data.total_exons})
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Size column */}
            <Grid item sx={{ height: "100%", width: "120px", flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography fontSize={"0.7rem"} color={"grey"}>
                    Size
                  </Typography>
                  <Typography fontSize={"0.8rem"} fontWeight={500}>
                    {formatSize(data.size)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Copy Number column */}
            <Grid item sx={{ height: "100%", width: "120px", flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography fontSize={"0.7rem"} color={"grey"}>
                    Copy Number
                  </Typography>
                  <Chip
                    label={data.copy_number || "N/A"}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      backgroundColor:
                        data.copy_number < 2
                          ? "#d32f2f"
                          : data.copy_number === 2
                          ? "#757575"
                          : "#f57c00",
                      color: "white",
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Quality Score column */}
            <Grid item sx={{ height: "100%", width: "120px", flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography fontSize={"0.7rem"} color={"grey"}>
                    Quality
                  </Typography>
                  <Typography fontSize={"0.8rem"}>
                    {data.quality_score
                      ? `${data.quality_score.toFixed(1)}%`
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* gnomAD SV AF column */}
            <Grid item sx={{ height: "100%", width: "140px", flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    fontSize={"0.7rem"}
                    color={"grey"}
                    textAlign="center"
                  >
                    gnomAD SV AF
                  </Typography>
                  <Typography fontSize={"0.8rem"}>
                    {data.gnomad_sv_af !== null &&
                    data.gnomad_sv_af !== undefined
                      ? parseFloat(data.gnomad_sv_af).toPrecision(2)
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Clinical Significance column */}
            <Grid item sx={{ height: "100%", width: "180px", flexShrink: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  py: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    maxHeight: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    fontSize={"0.7rem"}
                    color={"grey"}
                    textAlign="center"
                    sx={{ flexShrink: 0 }}
                  >
                    Clinical Significance
                  </Typography>
                  <Tooltip title={data.clinical_significance || "N/A"} arrow>
                    <Typography
                      fontSize={"0.75rem"}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        maxWidth: "160px",
                        color: getSignificanceColor(data.clinical_significance),
                        fontWeight: 500,
                      }}
                    >
                      {data.clinical_significance || "N/A"}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ overflow: "auto" }}>
          <CNVDetailTabs cnv={props.row} bam_files={props.bam_files} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
